import type { ReactNode } from 'react';

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import type { NormalizedApiError } from '@/shared/api';
import { getIntlLocale } from '@/shared/i18n';
import { ErrorState, LoadingState } from '@/shared/ui';

import type { VersionedSettings } from '../../model/settingsTypes';

interface SettingsScaffoldCommonProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Whether the current employee may view this domain. */
  canView: boolean;
  /** Whether the current employee may edit/save this domain. */
  canManage: boolean;
  /** Loaded resource audit info, used for the version/updated header chip. */
  audit?: VersionedSettings | null;
  /** Save-bar wiring (omit on read-only or action-only screens). */
  dirty?: boolean;
  saving?: boolean;
  onSave?: () => void;
  onReset?: () => void;
  /** Label used in the save-bar primary action. */
  saveLabel?: string;
  /**
   * Overrides the sticky bar's "You have unsaved changes" text while `dirty` — e.g. naming the
   * dirty scopes on a multi-resource page ("You have unsaved changes (General, MAX)",
   * design-chatbot-settings-page.md §11). Falls back to the generic copy when omitted.
   */
  dirtyMessage?: ReactNode;
  /**
   * Blocks the Save action without hiding it or changing the dirty/clean copy — for a client-side
   * validation error the merchant must fix before saving (a range or cross-field check the contract
   * would otherwise 422 on). Independent of `dirty`/`saving`.
   */
  saveDisabled?: boolean;
  /** Optional feedback element (e.g. a `<FeedbackSnackbar />`). */
  feedback?: ReactNode;
}

export type SettingsScaffoldProps<T> =
  | (SettingsScaffoldCommonProps & {
      /** Primary settings query for this domain. */
      query: UseQueryResult<T, NormalizedApiError>;
      /** Renders with the loaded data once the query succeeds. */
      children: (data: T) => ReactNode;
    })
  | (SettingsScaffoldCommonProps & {
      /**
       * Omit when this screen has no single primary resource to gate on (e.g. a page whose
       * content is itself a set of independently-loading tabs — see `RetailSettingsPage`). The
       * scaffold then skips straight to the permission gate and renders `children` unconditionally,
       * exactly as if a query had already succeeded.
       */
      query?: undefined;
      children: ReactNode;
    });

/** Narrows the {@link SettingsScaffoldProps} union on whether a `query` was supplied. */
function hasQuery<T>(
  props: SettingsScaffoldProps<T>,
): props is Extract<SettingsScaffoldProps<T>, { query: UseQueryResult<T, NormalizedApiError> }> {
  return props.query !== undefined;
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  // Follows the app's own language switcher (via getIntlLocale) rather than the browser/OS
  // locale that a bare `date.toLocaleString()` would use.
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Shared shell for every settings screen.
 *
 * Owns the cross-cutting concerns so each domain page only describes its fields:
 * a back link, version/updated header chips, the permission gate (no-access /
 * read-only), the load / error / content states, and a sticky save bar.
 */
export function SettingsScaffold<T>(props: SettingsScaffoldProps<T>) {
  const {
    title,
    subtitle,
    icon,
    canView,
    canManage,
    audit,
    dirty = false,
    saving = false,
    onSave,
    onReset,
    saveLabel,
    dirtyMessage,
    saveDisabled = false,
    feedback,
  } = props;
  const { t } = useTranslation('settings');
  const updatedAt = formatTimestamp(audit?.updatedAt);
  // A screen with no primary `query` (see the prop doc) has nothing to load — it is treated as
  // already "succeeded" for save-bar/body purposes, gated only by the permission flags below.
  const querySucceeded = !hasQuery(props) || props.query.isSuccess;
  const showSaveBar = canView && canManage && Boolean(onSave) && querySucceeded;
  const resolvedSaveLabel = saveLabel ?? t('scaffold.saveLabel');

  let body: ReactNode;
  if (!canView) {
    body = (
      <ErrorState
        title={t('scaffold.accessRestrictedTitle')}
        message={t('scaffold.accessRestrictedMessage')}
      />
    );
  } else if (hasQuery(props) && props.query.isLoading) {
    body = <LoadingState label={t('scaffold.loading')} />;
  } else if (hasQuery(props) && props.query.isError) {
    // The BFF returns HTTP 503 / code "owner-unavailable" when the owning
    // service is physically down (circuit-breaker opened or bounded timeout
    // exceeded). This is a contained, per-domain failure — the rest of the
    // settings hub and nav must remain fully functional.
    const { query } = props;
    const isOwnerUnavailable =
      query.error?.status === 503 || query.error?.code === 'owner-unavailable';

    body = (
      <ErrorState
        title={
          isOwnerUnavailable ? t('scaffold.serviceUnavailableTitle') : t('scaffold.loadErrorTitle')
        }
        message={
          isOwnerUnavailable
            ? t('scaffold.serviceUnavailableMessage')
            : (query.error?.message ?? t('common:error.unknown'))
        }
        onRetry={() => void query.refetch()}
      />
    );
  } else if (querySucceeded) {
    let content: ReactNode;
    if (hasQuery(props)) {
      // `querySucceeded` already guarantees `props.query.isSuccess` here — re-checking it
      // (rather than trusting that boolean-algebra fact) is what lets TS narrow
      // `UseQueryResult`'s own discriminated union so `.data` is `T`, not `T | undefined`.
      content = props.query.isSuccess ? props.children(props.query.data) : null;
    } else {
      content = props.children;
    }
    body = (
      <Stack spacing={2.5}>
        {!canManage ? (
          <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>
            {t('scaffold.readOnlyNotice')}
          </Alert>
        ) : null}
        {content}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        maxWidth: 1100,
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Button
        component={RouterLink}
        to="/settings"
        size="small"
        color="inherit"
        startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
        sx={{ mb: 2, ml: -1, color: 'text.secondary' }}
      >
        {t('scaffold.backToAllSettings')}
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {icon ? (
            <Box
              aria-hidden
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 44,
                height: 44,
                borderRadius: 2.5,
                flexShrink: 0,
                color: '#fff',
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1">
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {audit ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={t('scaffold.versionChip', { version: audit.version })}
            />
            {updatedAt ? (
              <Chip
                size="small"
                variant="outlined"
                label={t('scaffold.updatedChip', { value: updatedAt })}
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      <Box sx={{ mt: 3 }}>{body}</Box>

      {showSaveBar ? (
        <Paper
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 3,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            boxShadow: '0 8px 28px rgba(16, 24, 40, 0.12)',
          }}
        >
          <Typography
            variant="body2"
            color={dirty ? 'text.primary' : 'text.secondary'}
            sx={{ flexGrow: 1, pl: 1 }}
          >
            {dirty ? (dirtyMessage ?? t('scaffold.unsavedChanges')) : t('scaffold.allChangesSaved')}
          </Typography>
          <Button onClick={onReset} disabled={!dirty || saving}>
            {t('common:action.reset')}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!dirty || saving || saveDisabled}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />
            }
          >
            {saving ? t('common:status.saving') : resolvedSaveLabel}
          </Button>
        </Paper>
      ) : null}

      {feedback}
    </Box>
  );
}
